import { FormEvent, useState, useEffect } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { Button, Input, Label, Checkbox, Separator, Badge } from "@hotelise/ui";
import { Home, Mail, Lock, ArrowRight, Eye, EyeOff, Sun, Moon } from "lucide-react";

const SignIn = (): JSX.Element => {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [cognitoUser, setCognitoUser] = useState<any>(null);
	const [isDarkMode, setIsDarkMode] = useState(false);
	const navigate = useNavigate();

	useEffect(() => {
		const stored = localStorage.getItem("theme");
		const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
		const dark = stored === "dark" || (!stored && prefersDark);
		setIsDarkMode(dark);
		document.documentElement.classList.toggle("dark", dark);
	}, []);

	const toggleTheme = () => {
		const next = !isDarkMode;
		setIsDarkMode(next);
		document.documentElement.classList.toggle("dark", next);
		localStorage.setItem("theme", next ? "dark" : "light");
	};

	const getAuthErrorMessage = (error: any): string => {
		const type = error?.code || error?.name || error?.__type;
		switch (type) {
			case "UserNotFoundException":
			case "NotAuthorizedException":
				return "Incorrect email or password.";
			case "UserNotConfirmedException":
				return "Please confirm your email before signing in.";
			case "PasswordResetRequiredException":
				return "Password reset required. Please reset your password.";
			case "InvalidPasswordException":
				return error?.message || "Password does not meet requirements.";
			default:
				return error?.message || "Something went wrong. Please try again.";
		}
	};

	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault();
		setErrorMessage("");

		try {
			setIsLoading(true);
			const user = await Auth.signIn(email.trim(), password);
			if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
				setCognitoUser(user);
			}
		} catch (error: any) {
			setErrorMessage(getAuthErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	};

	const handleSetNewPassword = async (event: FormEvent) => {
		event.preventDefault();
		setErrorMessage("");

		try {
			setIsLoading(true);
			await Auth.completeNewPassword(cognitoUser, newPassword);
		} catch (error: any) {
			setErrorMessage(getAuthErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen w-full flex">
			{/* Left Panel - Branding (hidden on mobile) */}
			<div className="hidden lg:flex lg:w-1/2 xl:w-3/5 bg-primary relative overflow-hidden">
				{/* Animated background shapes */}
				<div className="absolute inset-0">
					<div className="absolute top-20 left-20 w-72 h-72 bg-primary-foreground/10 rounded-full blur-3xl animate-pulse" />
					<div className="absolute bottom-40 right-20 w-96 h-96 bg-primary-foreground/5 rounded-full blur-3xl animate-pulse delay-1000" />
					<div className="absolute top-1/2 left-1/3 w-64 h-64 bg-primary-foreground/10 rounded-full blur-2xl animate-pulse delay-500" />
				</div>

				{/* Grid pattern overlay */}
				<div
					className="absolute inset-0 opacity-10"
					style={{
						backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
						backgroundSize: "60px 60px",
					}}
				/>

				{/* Content */}
				<div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 text-primary-foreground">
					<div className="flex items-center gap-3">
						<div className="w-12 h-12 bg-primary-foreground/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
							<Home className="h-7 w-7" />
						</div>
						<div>
							<span className="text-2xl font-bold tracking-tight">Hotelise</span>
							<p className="text-sm text-primary-foreground/70 -mt-0.5">Owner Portal</p>
						</div>
					</div>

					<div className="space-y-8 max-w-lg">
						<div className="space-y-4">
							<h1 className="text-4xl xl:text-5xl font-bold leading-tight">
								Your property, always in view
							</h1>
							<p className="text-lg xl:text-xl text-primary-foreground/80 leading-relaxed">
								Track performance, review statements, and stay on top of your investment — all in one place.
							</p>
						</div>

						{/* Feature highlights */}
						<div className="grid gap-4">
							{[
								"Live reservation and occupancy tracking",
								"Statements, expenses & payout reports",
								"Multi-property dashboard with full transparency",
							].map((feature) => (
								<div key={feature} className="flex items-center gap-3">
									<div className="w-2 h-2 rounded-full bg-primary-foreground" />
									<span className="text-primary-foreground/90">{feature}</span>
								</div>
							))}
						</div>
					</div>

					<p className="text-sm text-primary-foreground/60">
						© 2026 Hotelise. All rights reserved.
					</p>
				</div>
			</div>

			{/* Right Panel - Login Form */}
			<div className="w-full lg:w-1/2 xl:w-2/5 flex flex-col bg-background relative">
				{/* Theme toggle */}
				<Button
					variant="ghost"
					size="icon"
					onClick={toggleTheme}
					className="absolute top-4 right-4 z-20"
					type="button"
					aria-label="Toggle theme"
					title="Toggle theme"
				>
					{isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
				</Button>

				{/* Mobile header */}
				<div className="lg:hidden p-6 pb-0">
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
							<Home className="h-5 w-5 text-primary-foreground" />
						</div>
						<div>
							<span className="text-xl font-bold">Hotelise</span>
							<p className="text-xs text-muted-foreground -mt-0.5">Owner Portal</p>
						</div>
					</div>
				</div>

				{/* Form container */}
				<div className="flex-1 flex items-center justify-center p-6 sm:p-8 lg:p-12">
					<div className="w-full max-w-sm space-y-8">
						{/* Header */}
						<div className="space-y-2 text-center lg:text-left">
							<h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
								{cognitoUser ? "Set new password" : "Welcome back"}
							</h2>
							<p className="text-muted-foreground">
								{cognitoUser
									? <>New password required for <span className="font-medium text-foreground">{email}</span></>
									: "Enter your credentials to access your account"}
							</p>
						</div>

						{!cognitoUser && (
							<>
								{/* Social login buttons */}
								<div className="grid grid-cols-2 gap-3">
									<Button variant="outline" className="h-11 relative" disabled type="button">
										<svg className="h-5 w-5 mr-2 opacity-50" viewBox="0 0 24 24">
											<path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
											<path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
											<path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
											<path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
										</svg>
										<span className="opacity-50">Google</span>
										<Badge variant="secondary" className="absolute -top-2 -right-2 text-[10px] px-1.5 py-0">Soon</Badge>
									</Button>
									<Button variant="outline" className="h-11 relative" disabled type="button">
										<svg className="h-5 w-5 mr-2 opacity-50" fill="currentColor" viewBox="0 0 24 24">
											<path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
										</svg>
										<span className="opacity-50">GitHub</span>
										<Badge variant="secondary" className="absolute -top-2 -right-2 text-[10px] px-1.5 py-0">Soon</Badge>
									</Button>
								</div>

								<div className="relative">
									<Separator />
									<span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-3 text-xs text-muted-foreground uppercase tracking-wider">
										or continue with email
									</span>
								</div>
							</>
						)}

						{/* Login / New Password form */}
						{cognitoUser ? (
							<form className="space-y-5" onSubmit={handleSetNewPassword}>
								<p className="text-sm text-muted-foreground">
									Must be at least 8 characters with uppercase, lowercase, and a number.
								</p>
								<div className="space-y-2">
									<Label htmlFor="newPassword">New Password</Label>
									<div className="relative">
										<Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
										<Input
											id="newPassword"
											type={showNewPassword ? "text" : "password"}
											placeholder="••••••••"
											className="pl-10 pr-10 h-11"
											value={newPassword}
											onChange={(e) => { setErrorMessage(""); setNewPassword(e.target.value); }}
										/>
										<Button
											type="button"
											variant="ghost"
											size="icon"
											className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground"
											onClick={() => setShowNewPassword(!showNewPassword)}
												aria-label={showNewPassword ? "Hide password" : "Show password"}
												title={showNewPassword ? "Hide password" : "Show password"}
										>
											{showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
										</Button>
									</div>
								</div>

								{errorMessage && (
									<div className="p-3 rounded-lg border border-destructive/50 bg-destructive/10 text-sm text-destructive" role="alert">
										{errorMessage}
									</div>
								)}

								<Button type="submit" className="w-full h-11 group" disabled={isLoading}>
									{isLoading ? "Setting password…" : "Set New Password"}
									<ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" />
								</Button>
							</form>
						) : (
							<form className="space-y-5" onSubmit={handleSubmit}>
								<div className="space-y-2">
									<Label htmlFor="email">Email</Label>
									<div className="relative">
										<Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
										<Input
											id="email"
											type="email"
											placeholder="owner@example.com"
											className="pl-10 h-11"
											value={email}
											onChange={(e) => { setErrorMessage(""); setEmail(e.target.value); }}
										/>
									</div>
								</div>

								<div className="space-y-2">
									<div className="flex items-center justify-between">
										<Label htmlFor="password">Password</Label>
										<Button
											variant="link"
											type="button"
											className="px-0 h-auto text-xs text-muted-foreground hover:text-primary"
											onClick={() => navigate("/forgot-password")}
										>
											Forgot password?
										</Button>
									</div>
									<div className="relative">
										<Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
										<Input
											id="password"
											type={showPassword ? "text" : "password"}
											placeholder="••••••••"
											className="pl-10 pr-10 h-11"
											value={password}
											onChange={(e) => { setErrorMessage(""); setPassword(e.target.value); }}
										/>
										<Button
											type="button"
											variant="ghost"
											size="icon"
											className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground"
											onClick={() => setShowPassword(!showPassword)}
												aria-label={showPassword ? "Hide password" : "Show password"}
												title={showPassword ? "Hide password" : "Show password"}
										>
											{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
										</Button>
									</div>
								</div>

								<div className="flex items-center gap-2">
									<Checkbox id="remember" />
									<Label htmlFor="remember" className="text-sm font-normal text-muted-foreground cursor-pointer">
										Remember me for 30 days
									</Label>
								</div>

								{errorMessage && (
									<div className="p-3 rounded-lg border border-destructive/50 bg-destructive/10 text-sm text-destructive" role="alert">
										{errorMessage}
									</div>
								)}

								<Button type="submit" className="w-full h-11 group" disabled={isLoading}>
									{isLoading ? "Signing in…" : "Sign in"}
									<ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" />
								</Button>
							</form>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default SignIn;
